import { config } from "@/config/env";
import type { RoundConfigurationsEntityType } from "EntityTypes";
import type { RoundConfiguration } from "Models";
import type {
  AppQueryConfig,
  RoundConfigurationToMultiSimsPayload,
} from "QueryTypes";
import { clubhouseApiHeadersV1 } from "utils/api/apiHelper";

const urls = {
  getAllRoundConfigs(facilityId: string): string {
    return `${config.apiBaseUrl}/api/facilities/${facilityId}/round-configuration/all`;
  },
  addRoundConfig(facilityId: string): string {
    return `${config.apiBaseUrl}/api/facilities/${facilityId}/round-configuration`;
  },
  deleteRoundConfig(facilityId: string, roundConfigId: string): string {
    return `${config.apiBaseUrl}/api/facilities/${facilityId}/round-configuration/${roundConfigId}`;
  },
  sendToMultipleSims(): string {
    return `${config.apiBaseUrl}/api/simulators/round-configuration/send`;
  },
};

export const getFacilityRoundConfigs = (
  facilityId: string,
): AppQueryConfig => ({
  url: urls.getAllRoundConfigs(facilityId),
  queryKey: `facilityRoundConfigs:${facilityId}`,
  options: { ...clubhouseApiHeadersV1() },
  transform: (responseJson: RoundConfiguration[]) => {
    const obj: { [id: string]: RoundConfiguration } = {};
    responseJson?.map((config) => (obj[config.id] = config));
    return {
      roundConfigurations: {
        byFacilityId: { [facilityId]: obj },
      },
    };
  },
  update: {
    roundConfigurations: (
      oldValue: RoundConfigurationsEntityType,
      newValue: RoundConfigurationsEntityType,
    ): RoundConfigurationsEntityType => ({
      ...oldValue,
      byFacilityId: {
        ...oldValue?.byFacilityId,
        ...newValue.byFacilityId,
      },
    }),
  },
});

export const makeAddRoundConfigurationMutation = (
  facilityId: string,
  body: {},
): AppQueryConfig => ({
  url: urls.addRoundConfig(facilityId),
  queryKey: `addRoundConfig:${facilityId}`,
  options: {
    ...clubhouseApiHeadersV1(),
    method: "POST",
  },
  body,
  transform: (responseJson: RoundConfiguration) => {
    return {
      roundConfigurations: {
        byFacilityId: {
          [facilityId]: { [responseJson.id]: responseJson },
        },
      },
    };
  },
  update: {
    roundConfigurations: (oldValue, newValue) => ({
      ...oldValue,
      byFacilityId: {
        ...oldValue?.byFacilityId,
        ...{
          [facilityId]: {
            ...oldValue.byFacilityId?.[facilityId],
            ...newValue.byFacilityId[facilityId],
          },
        },
      },
    }),
  },
});

export const makeDeleteRoundConfigurationMutation = (
  facilityId: string,
  roundConfigId: string,
): AppQueryConfig => ({
  url: urls.deleteRoundConfig(facilityId, roundConfigId),
  queryKey: `deleteRoundConfig:${roundConfigId}`,
  options: {
    ...clubhouseApiHeadersV1(),
    method: "DELETE",
  },
  optimisticUpdate: {
    roundConfigurations: (reduxState) => {
      const byFacilityId = reduxState.byFacilityId[facilityId];
      if (byFacilityId) {
        delete byFacilityId[roundConfigId];
      }
      return reduxState;
    },
  },
});

export const makeSendRoundConfigurationToSimulatorsMutation = (
  body: RoundConfigurationToMultiSimsPayload,
): AppQueryConfig => ({
  url: urls.sendToMultipleSims(),
  options: {
    ...clubhouseApiHeadersV1(),
    method: "POST",
  },
  body,
});
